<script setup lang="ts">
import type { EventDetails, Price, ProductDetails } from "mws-ts-v2-sdk";

import { BidAttemptType, BidModalEnum } from "~/types/Catalog";

type Props = {
  product: ProductDetails;
  event: EventDetails;
  bidChoices: Price[][];
  disabled: boolean;
};

const { product, event, bidChoices } = defineProps<Props>();
const quickBidChoices = computed(() => {
  return bidChoices.map((pricesWithCurrencies, index) => ({
    prices: pricesWithCurrencies,
    identifier:
      pricesWithCurrencies.find(
        (priceInCurrency) => priceInCurrency.currency === "EUR",
      )?.price ?? index,
  }));
});

const emit = defineEmits<{
  (e: "active-currency-missing"): void;
  (e: "update-disabled-state", disabledState: boolean): void;
}>();

const {
  fullBidAmount,
  bidAmountInCents,
  getAmountFromChoice,
  isSubmitBidPending,
  isBiddingDisabled,
  initializeBiddingAttempt,
  bidAttemptType,
  submitBid,
  activeCurrency,
  bidModalType,
  closeBidModal,
  paymentTokenId,
  paymentTokenSelected,
  raiseBidAmount,
} = useBidding(
  {
    product: () => product,
    event: () => event,
    bidTypes: [BidAttemptType.Quickbid],
  },
  (disabledState) => emit("update-disabled-state", disabledState),
);

const submitForm = (choice: Price[]): void => {
  bidAmountInCents.value = getAmountFromChoice(choice);
  initializeBiddingAttempt({
    bidAttemptType: BidAttemptType.Quickbid,
  });
};
</script>

<template>
  <div
    data-cy="quick-bid"
    class="pb-2"
  >
    <div
      class="grid grid-cols-3 gap-2"
      data-cy="quick-bid-choices"
    >
      <div
        v-for="choice in quickBidChoices"
        :key="choice.identifier"
        class="grow"
      >
        <BaseButton
          data-cy="quick-bid-choice"
          :disabled="isBiddingDisabled || disabled"
          :block="true"
          color-scheme="secondary"
          :cta="true"
          class="w-full"
          @click="submitForm(choice.prices)"
        >
          <BaseCurrency
            :iso="activeCurrency.isoCurrencyCode"
            :value="getAmountFromChoice(choice.prices)"
            :is-in-cents="true"
          />
        </BaseButton>
      </div>
    </div>

    <ProductBidModal
      v-if="bidModalType"
      :type="bidModalType"
      :product="product"
      :bid-attempt="{
        type: bidAttemptType,
        loading: isSubmitBidPending,
        amount: fullBidAmount,
        currency: activeCurrency.isoCurrencyCode,
      }"
      :payment-token-id="paymentTokenId"
      @confirm="submitBid"
      @close="closeBidModal"
      @continue-with-payment-token="paymentTokenSelected"
      @back-to-card-overview="bidModalType = BidModalEnum.ReservationRequired"
      @raise-bid="raiseBidAmount"
    />
  </div>
</template>
